import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { LiveNowBanner } from "@/components/LiveNowBanner";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PlayCircle, PenLine, Flag, UserX, ExternalLink, Rss, Podcast, Briefcase, ShoppingBag, BookOpen, Link as LinkIcon, Heart } from "lucide-react";
import { SECTIONS } from "@/lib/sections";

const linkIconFor = (label) => {
  const l = (label || "").toLowerCase();
  if (l.includes("newsletter") || l.includes("substack")) return <Rss size={13} strokeWidth={1.5} />;
  if (l.includes("podcast")) return <Podcast size={13} strokeWidth={1.5} />;
  if (l.includes("portfolio")) return <Briefcase size={13} strokeWidth={1.5} />;
  if (l.includes("shop") || l.includes("etsy")) return <ShoppingBag size={13} strokeWidth={1.5} />;
  if (l.includes("book")) return <BookOpen size={13} strokeWidth={1.5} />;
  if (l.includes("website")) return <ExternalLink size={13} strokeWidth={1.5} />;
  return <LinkIcon size={13} strokeWidth={1.5} />;
};

const displayHost = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const fmtBroadcastDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

const fmtJoined = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "";

const Profile = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("posts");

  const load = async () => {
    setLoading(true);
    try {
      const [p, postsRes] = await Promise.all([
        api.get(`/users/${username}`),
        api.get(`/users/${username}/posts`),
      ]);
      setProfile(p.data);
      setPosts(postsRes.data);
      // Fetch broadcasts only if this profile is an editor
      if (p.data?.is_editor) {
        try {
          const br = await api.get(`/live/broadcasts`, { params: { editor_id: p.data.id, limit: 50 } });
          // Honor per-broadcast profile visibility
          setBroadcasts((br.data || []).filter((b) => b?.visibility?.profile !== false));
        } catch {
          setBroadcasts([]);
        }
      } else {
        setBroadcasts([]);
      }
    } catch {
      toast.error("Profile not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [username]);

  const toggleFollow = async () => {
    if (!user) { navigate("/login"); return; }
    const { data } = await api.post(`/users/${profile.id}/follow`);
    setProfile({ ...profile, is_following: data.following, followers: profile.followers + (data.following ? 1 : -1) });
  };

  const startConversation = async () => {
    if (!user) { navigate("/login"); return; }
    const { data } = await api.post("/conversations", { user_id: profile.id });
    navigate(`/messages/${data.id}`);
  };

  if (loading) return (
    <div className="app-shell"><Navbar /><div className="container-belle" style={{ paddingTop: 140 }}>Loading…</div></div>
  );
  if (!profile) return null;

  const isMe = user && user.id === profile.id;

  return (
    <div data-testid="profile-page" className="app-shell">
      <Navbar />
      <LiveNowBanner />
      <div className="container-belle" style={{ paddingTop: 110, paddingBottom: 48 }}>
        {/* HEADER */}
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 56, alignItems: "center", marginBottom: 64 }} className="profile-header">
          <div>
            {profile.avatar_url ? (
              <img
                loading="lazy" decoding="async" src={profile.avatar_url}
                alt={profile.username}
                style={{ width: 200, height: 200, objectFit: "cover", borderRadius: "50%", border: "1px solid var(--border-default)" }}
              />
            ) : (
              <div style={{ width: 200, height: 200, borderRadius: "50%", background: "var(--bg-alt)" }} />
            )}
          </div>
          <div>
            <div className="overline" style={{ color: "var(--brand-accent)" }}>@{profile.username}{profile.is_subscriber && " · Subscriber"}</div>
            <h1 className="editorial-h1" data-testid="profile-display-name" style={{ fontSize: "clamp(40px, 5.5vw, 64px)", marginTop: 10, marginBottom: 14 }}>
              {profile.display_name}
            </h1>
            {profile.location && <div className="overline" style={{ marginBottom: 18 }}>{profile.location}</div>}
            {profile.bio && (
              <p className="font-serif" style={{ fontSize: 21, fontStyle: "italic", lineHeight: 1.45, color: "var(--text-secondary)", maxWidth: 600, margin: 0 }}>
                {profile.bio}
              </p>
            )}
            <div style={{ display: "flex", gap: 36, marginTop: 28, alignItems: "center" }}>
              <Stat n={profile.posts_count} label="Posts" />
              <Stat n={profile.followers} label="Followers" />
              <Stat n={profile.following} label="Following" />
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 28 }}>
              {isMe ? (
                <Link to="/settings" data-testid="edit-profile-button" className="btn-secondary" style={{ textDecoration: "none" }}>
                  Edit profile
                </Link>
              ) : (
                <>
                  <button onClick={toggleFollow} data-testid="follow-button" className={profile.is_following ? "btn-secondary" : "btn-primary"}>
                    {profile.is_following ? "Following" : "Follow"}
                  </button>
                  <button
                    onClick={startConversation}
                    data-testid="message-button"
                    className="btn-primary"
                    style={{ background: "var(--brand-accent)", display: "inline-flex", alignItems: "center", gap: 8 }}
                  >
                    <PenLine size={14} strokeWidth={1.5} /> Write a letter
                  </button>
                  <ProfileMenu profile={profile} />
                </>
              )}
            </div>
          </div>
        </div>

        <div className="divider-line" style={{ marginBottom: 32 }} />

        {/* TABS */}
        <div
          role="tablist"
          data-testid="profile-tabs"
          style={{
            display: "flex",
            gap: 36,
            borderBottom: "1px solid var(--border-default)",
            marginBottom: 32,
            flexWrap: "wrap",
          }}
        >
          <TabBtn id="posts" label="Posts" count={posts.length} active={activeTab === "posts"} onClick={() => setActiveTab("posts")} />
          {profile.is_editor && (
            <TabBtn id="broadcasts" label="Broadcasts" count={broadcasts.length} active={activeTab === "broadcasts"} onClick={() => setActiveTab("broadcasts")} />
          )}
          <TabBtn id="about" label="About" active={activeTab === "about"} onClick={() => setActiveTab("about")} />
        </div>

        {/* POSTS */}
        {activeTab === "posts" && (
          posts.length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", fontSize: 20 }}>
              No posts yet — the page is still being written.
            </p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }} className="profile-grid">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/post/${p.id}`}
                  data-testid={`profile-tile-${p.id}`}
                  className="media-zoom"
                  style={{ display: "block", aspectRatio: "4/5", overflow: "hidden", background: "var(--bg-alt)" }}
                >
                  {p.media_url ? (
                    p.media_type === "video" ? (
                      <video src={p.media_url} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <img loading="lazy" decoding="async" src={p.media_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    )
                  ) : (
                    <div style={{ padding: 24, display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
                      <div className="overline" style={{ color: "var(--brand-accent)" }}>Letter</div>
                      <p className="font-serif" style={{ fontSize: 17, fontStyle: "italic", marginTop: 10, lineHeight: 1.3 }}>{p.title}</p>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )
        )}

        {/* BROADCASTS */}
        {activeTab === "broadcasts" && profile.is_editor && (
          broadcasts.length === 0 ? (
            <p data-testid="broadcasts-empty" style={{ color: "var(--text-muted)", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", fontSize: 20 }}>
              No broadcasts yet — when {isMe ? "you go" : `${profile.display_name} goes`} live, the replays will be gathered here.
            </p>
          ) : (
            <div
              data-testid="broadcasts-grid"
              style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 24 }}
              className="broadcasts-grid"
            >
              {broadcasts.map((b) => (
                <Link
                  key={b.id}
                  to={`/broadcast/${b.id}`}
                  data-testid={`profile-broadcast-${b.id}`}
                  className="media-zoom"
                  style={{ display: "block", textDecoration: "none", color: "var(--text-primary)" }}
                >
                  <div style={{ position: "relative", aspectRatio: "16 / 9", background: "var(--bg-alt)", overflow: "hidden" }}>
                    {b.custom_thumbnail_url ? (
                      <img loading="lazy" decoding="async" src={b.custom_thumbnail_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : b.asset_playback_id ? (
                      <img
                        loading="lazy" decoding="async" src={`https://image.mux.com/${b.asset_playback_id}/thumbnail.jpg?width=720&time=${b.thumbnail_time || 2}`}
                        alt=""
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "var(--text-muted)", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
                        Finishing…
                      </div>
                    )}
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 60%, rgba(51,51,51,0.55) 100%)" }} />
                    <PlayCircle
                      size={48}
                      strokeWidth={1.2}
                      color="#FAF9F5"
                      style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", opacity: 0.9 }}
                    />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div className="overline" style={{ color: "var(--brand-accent)" }}>
                      {b.section || "Live"} · {fmtBroadcastDate(b.ended_at)}
                    </div>
                    <h3 className="font-serif" style={{ fontSize: 22, marginTop: 6, lineHeight: 1.25 }}>
                      {b.title || "Untitled broadcast"}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          )
        )}

        {/* ABOUT */}
        {activeTab === "about" && (
          <div data-testid="profile-about" style={{ maxWidth: 640 }}>
            <div className="overline" style={{ color: "var(--brand-accent)", marginBottom: 10 }}>About</div>
            {profile.bio ? (
              <p className="font-serif" style={{ fontSize: 22, fontStyle: "italic", lineHeight: 1.5, color: "var(--text-secondary)", marginBottom: 28 }}>
                {profile.bio}
              </p>
            ) : (
              <p style={{ color: "var(--text-muted)", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", fontSize: 18, marginBottom: 28 }}>
                No bio yet.
              </p>
            )}
            {(profile.links && profile.links.length > 0) && (
              <div data-testid="profile-links" style={{ marginBottom: 28 }}>
                <div className="overline" style={{ color: "var(--text-muted)", marginBottom: 10 }}>Elsewhere</div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                  {profile.links.map((link, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: "var(--brand-accent)", display: "inline-flex" }}>
                        {linkIconFor(link.label)}
                      </span>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        data-testid={`profile-link-${i}`}
                        style={{
                          fontFamily: "'Cormorant Garamond', serif",
                          fontSize: 17,
                          color: "var(--text-primary)",
                          textDecoration: "none",
                          borderBottom: "1px solid var(--border-default)",
                          paddingBottom: 1,
                        }}
                      >
                        <span style={{ color: "var(--text-muted)", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginRight: 10, fontFamily: "'Outfit', sans-serif" }}>
                          {link.label}
                        </span>
                        {displayHost(link.url)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {profile.favorite_rooms && profile.favorite_rooms.length > 0 && (
              <div data-testid="profile-favorite-rooms" style={{ marginBottom: 28 }}>
                <div className="overline" style={{ color: "var(--text-muted)", marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Heart size={12} strokeWidth={1.6} fill="var(--brand-accent)" color="var(--brand-accent)" />
                  Favorite rooms
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {profile.favorite_rooms.map((slug) => {
                    const s = SECTIONS.find((x) => x.slug === slug);
                    if (!s) return null;
                    return (
                      <Link
                        key={slug}
                        to={`/room/${slug}`}
                        data-testid={`profile-favorite-room-${slug}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 12px",
                          border: "1px solid var(--brand-accent)",
                          color: "var(--brand-accent)",
                          fontSize: 11,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          fontFamily: "'Outfit', sans-serif",
                          fontWeight: 500,
                          textDecoration: "none",
                          borderRadius: 24,
                          background: "rgba(114, 47, 55, 0.06)",
                        }}
                      >
                        {s.label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
            <dl style={{ display: "grid", gridTemplateColumns: "120px 1fr", rowGap: 14, columnGap: 24, fontSize: 14 }}>
              {profile.location && (<>
                <dt className="overline" style={{ color: "var(--text-muted)" }}>Location</dt>
                <dd style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, margin: 0 }}>{profile.location}</dd>
              </>)}
              {profile.created_at && (<>
                <dt className="overline" style={{ color: "var(--text-muted)" }}>Joined</dt>
                <dd style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, margin: 0 }}>{fmtJoined(profile.created_at)}</dd>
              </>)}
              {profile.is_editor && (<>
                <dt className="overline" style={{ color: "var(--text-muted)" }}>Role</dt>
                <dd style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, margin: 0, color: "var(--brand-accent)" }}>Editor</dd>
              </>)}
              {profile.is_subscriber && (<>
                <dt className="overline" style={{ color: "var(--text-muted)" }}>Subscriber</dt>
                <dd style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, margin: 0 }}>Yes</dd>
              </>)}
            </dl>
          </div>
        )}
      </div>
      <Footer />
      <style>{`
        @media (max-width: 768px) {
          .profile-header { grid-template-columns: 1fr !important; gap: 24px !important; text-align: left; }
          .profile-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .broadcasts-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

const Stat = ({ n, label }) => (
  <div>
    <div className="font-serif" style={{ fontSize: 28, fontWeight: 500 }}>{n}</div>
    <div className="overline" style={{ marginTop: 2 }}>{label}</div>
  </div>
);

const ProfileMenu = ({ profile }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const toggleBlock = async () => {
    if (busy) return;
    const currentlyBlocked = !!profile.is_blocked;
    // eslint-disable-next-line no-alert
    if (!currentlyBlocked && !window.confirm(`Block ${profile.display_name}? They won't be able to write to you or see your future letters.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${profile.id}/block`);
      toast.success(data.blocked ? "Blocked." : "Unblocked.");
      profile.is_blocked = data.blocked; // eslint-disable-line no-param-reassign
      setOpen(false);
    } catch {
      toast.error("Could not update.");
    } finally {
      setBusy(false);
    }
  };

  const submitReport = async () => {
    if (!reportText.trim()) {
      toast.error("Please describe your concern.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/reports", { target_type: "user", target_id: profile.id, reason: reportText.trim() });
      toast.success("Report received — thank you.");
      setReportText("");
      setReportOpen(false);
    } catch {
      toast.error("Could not send.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        data-testid="profile-menu-toggle"
        className="btn-secondary"
        style={{ padding: "8px 14px", fontSize: 12, letterSpacing: "0.15em" }}
        aria-label="More options"
      >
        ⋯
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 50,
            minWidth: 200,
            background: "var(--surface)",
            border: "1px solid var(--border-default)",
            boxShadow: "0 10px 24px rgba(51,51,51,0.1)",
            padding: 6,
          }}
        >
          <button
            type="button"
            onClick={() => { setReportOpen(true); setOpen(false); }}
            data-testid="report-user-btn"
            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px", background: "transparent", border: "none", cursor: "pointer", color: "var(--text-primary)", fontSize: 13, fontFamily: "'Outfit', sans-serif" }}
          >
            <Flag size={13} strokeWidth={1.5} /> Report
          </button>
          <button
            type="button"
            onClick={toggleBlock}
            data-testid="block-user-btn"
            disabled={busy}
            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px", background: "transparent", border: "none", cursor: "pointer", color: "var(--brand-accent)", fontSize: 13, fontFamily: "'Outfit', sans-serif" }}
          >
            <UserX size={13} strokeWidth={1.5} /> {profile.is_blocked ? "Unblock" : "Block"}
          </button>
        </div>
      )}
      {reportOpen && (
        <div
          onClick={() => setReportOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(51,51,51,0.5)",
            display: "grid", placeItems: "center", zIndex: 100, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--surface)", padding: 32, maxWidth: 480, width: "100%", border: "1px solid var(--border-default)" }}
          >
            <div className="overline" style={{ color: "var(--brand-accent)" }}>Report</div>
            <h3 className="font-serif" style={{ fontSize: 24, marginTop: 8, fontWeight: 500 }}>
              Tell us what&apos;s concerning
            </h3>
            <p className="editorial-body" style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 8, fontStyle: "italic" }}>
              Your report is confidential. The editor reviews each report personally.
            </p>
            <textarea
              data-testid="report-reason"
              value={reportText}
              onChange={(e) => setReportText(e.target.value.slice(0, 500))}
              placeholder="What happened?"
              rows={4}
              className="input-belle"
              style={{ width: "100%", marginTop: 14, fontFamily: "'Cormorant Garamond', serif", fontSize: 15 }}
            />
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 18 }}>
              <button type="button" className="btn-secondary" onClick={() => setReportOpen(false)}>Cancel</button>
              <button
                type="button"
                onClick={submitReport}
                disabled={busy}
                data-testid="submit-report"
                className="btn-primary"
                style={{ background: "var(--brand-accent)" }}
              >
                Send report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TabBtn = ({ id, label, count, active, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    data-testid={`profile-tab-${id}`}
    style={{
      background: "transparent",
      border: "none",
      cursor: "pointer",
      padding: "8px 0 16px 0",
      fontSize: 12,
      letterSpacing: "0.2em",
      textTransform: "uppercase",
      fontWeight: 500,
      color: active ? "var(--brand-accent)" : "var(--text-muted)",
      borderBottom: active ? "2px solid var(--brand-accent)" : "2px solid transparent",
      marginBottom: -1,
      transition: "color 0.2s ease, border-color 0.2s ease",
    }}
  >
    {label}
    {count !== undefined && count > 0 && (
      <span style={{ marginLeft: 8, color: "var(--text-muted)", fontWeight: 400 }}>{count}</span>
    )}
  </button>
);

export default Profile;
